import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronRight, Search, Send, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { PageHeader, BrandButton } from "@/components/PageHeader";
import { Pager, usePaged } from "@/components/Pager";
import { DispatchTable, GROUPS, canSend } from "@/components/certificates/DispatchTable";
import { CertNameDialog } from "@/components/certificates/CertNameDialog";
import { api, errMsg } from "@/lib/api";

const EventRow = ({ ev, onOpen }) => (
  <button onClick={onOpen} className="w-full text-left bg-white border border-[#E5E7EB] rounded-lg p-4 flex items-center gap-4 hover:border-[#E60000] transition-colors" data-testid={`cd-event-${ev.id}`}>
    <div className="flex-1 min-w-0">
      <div className="font-heading font-bold truncate">{ev.title}{ev.archived && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 rounded px-1.5 py-0.5">Archived</span>}</div>
      <div className="text-xs text-gray-500">{[ev.date, ev.venue].filter(Boolean).join(" · ")}</div>
      {!ev.template_ready && <div className="text-xs text-amber-700 mt-1">No certificate template yet</div>}
    </div>
    <div className="grid grid-cols-2 gap-4 text-center text-xs">
      <div><div className="font-heading text-lg font-bold" data-testid={`cd-event-${ev.id}-registered`}>{ev.registered}</div><div className="text-gray-500">Registered</div></div>
      <div><div className="font-heading text-lg font-bold text-emerald-700" data-testid={`cd-event-${ev.id}-sent`}>{ev.sent}</div><div className="text-gray-500">Sent</div></div>
    </div>
    <ChevronRight className="w-4 h-4 text-gray-400" />
  </button>
);

const Filters = ({ groups, setGroups, onlyIn, setOnlyIn }) => (
  <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
    {GROUPS.map(([k, l]) => (
      <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={groups.includes(k)} onCheckedChange={(v) => setGroups((g) => (v ? [...g, k] : g.filter((x) => x !== k)))} data-testid={`cd-group-${k}`} />{l}
      </label>
    ))}
    <label className="flex items-center gap-2 text-sm cursor-pointer ml-auto"><Switch checked={onlyIn} onCheckedChange={setOnlyIn} data-testid="cd-only-checked-in" />Only people who checked in</label>
  </div>
);

const EventView = ({ eid }) => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState(GROUPS.map(([k]) => k));
  const [onlyIn, setOnlyIn] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [naming, setNaming] = useState(null);
  const load = useCallback(() => api.get(`/cert-dispatch/${eid}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [eid]);
  useEffect(() => { load(); }, [load]);
  const sending = data?.rows.some((r) => r.cert?.sending);
  useEffect(() => {
    if (!sending) { return undefined; }
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [sending, load]);
  const visible = useMemo(() => (data?.rows || []).filter((r) => groups.includes(r.group) && (!onlyIn || r.checked_in)), [data, groups, onlyIn]);
  const rows = useMemo(() => visible.filter((r) => `${r.name} ${r.certificate_name} ${r.ticket_no}`.toLowerCase().includes(q.toLowerCase())), [visible, q]);
  const pending = visible.filter((r) => canSend(r) && !r.cert?.sent_at).length;
  const pg = usePaged(rows, `${q}|${groups}|${onlyIn}`);
  const send = async (payload) => {
    setBusy(true);
    try {
      const r = (await api.post(`/cert-dispatch/${eid}/send`, { groups, checked_in_only: onlyIn, ...payload })).data;
      const skipped = [r.unpaid && `${r.unpaid} not fully paid`, r.no_email && `${r.no_email} without email`].filter(Boolean).join(", ");
      toast.success(`${r.queued} certificate${r.queued === 1 ? "" : "s"} being emailed${skipped ? ` · skipped ${skipped}` : ""}`);
      setSelected(new Set());
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const check = (id, v) => setSelected((s) => { const n = new Set(s); if (v) { n.add(id); } else { n.delete(id); } return n; });
  const checkAll = (list, v) => setSelected((s) => { const n = new Set(s); list.forEach((r) => (v ? n.add(r.id) : n.delete(r.id))); return n; });
  if (!data) { return <div className="text-gray-500">Loading…</div>; }
  return <>
    {!data.template_ready && <div className="mb-4 flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3" data-testid="cd-no-template"><AlertTriangle className="w-4 h-4" />Set up this event's certificate in Control → Certificate Studio before sending.</div>}
    <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
      <div className="p-3 border-b space-y-3">
        <Filters groups={groups} setGroups={setGroups} onlyIn={onlyIn} setOnlyIn={setOnlyIn} />
        <div className="flex flex-col md:flex-row gap-2">
          <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, certificate name or ticket number" className="pl-9 h-10" data-testid="cd-search" /></div>
          <BrandButton variant="outline" onClick={() => send({ mode: "selected", txn_ids: [...selected] })} disabled={busy || !selected.size || !data.template_ready} data-testid="cd-send-selected">
            <Send className="w-4 h-4" />Send to selected ({selected.size})
          </BrandButton>
          <BrandButton onClick={() => send({ mode: "all" })} disabled={busy || !pending || !data.template_ready} data-testid="cd-send-all">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send to all eligible ({pending})
          </BrandButton>
        </div>
      </div>
      <div className="overflow-x-auto"><DispatchTable rows={pg.items} selected={selected} onCheck={check} onCheckAll={checkAll} onSend={(r) => send({ mode: "selected", txn_ids: [r.id] })} onEditName={setNaming} busy={busy || !data.template_ready} /></div>
      {naming && <CertNameDialog eventId={eid} row={naming} onClose={() => setNaming(null)} onSaved={() => { setNaming(null); load(); }} />}
      {!rows.length && <div className="p-10 text-center text-sm text-gray-500" data-testid="cd-empty">{data.rows.length ? "No participants match your filters." : "No submitted registrations for this event yet."}</div>}
      <Pager {...pg} testId="cd-pager" />
    </div>
  </>;
};

export default function CertificateDispatch() {
  const [events, setEvents] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!openId) { api.get("/cert-dispatch").then((r) => setEvents(r.data)).catch(() => setEvents([])); }
  }, [openId]);
  const current = openId && events?.find((e) => e.id === openId);
  const list = (events || []).filter((e) => e.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="cert-dispatch-page">
      <PageHeader eyebrow="Event Registration" title={current ? current.title : "Certificate Dispatch"} description={current ? "Email PDF certificates to paid members, protocol, sponsors and students / interns. Sent certificates get a green mark; use Resend to send again." : "Pick an event to email participant certificates."}
        actions={current && <BrandButton variant="outline" onClick={() => setOpenId(null)} data-testid="cd-back"><ArrowLeft className="w-4 h-4" />All events</BrandButton>} />
      {!events ? <div className="text-gray-500">Loading…</div> : current ? <EventView eid={current.id} /> : <>
        <div className="relative mb-4 max-w-md"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an event" className="pl-9 h-10 bg-white" data-testid="cd-event-search" /></div>
        <div className="space-y-3">
          {list.map((ev) => <EventRow key={ev.id} ev={ev} onOpen={() => setOpenId(ev.id)} />)}
          {!list.length && <div className="p-10 text-center text-sm text-gray-500 bg-white border rounded-lg" data-testid="cd-no-events">No events found.</div>}
        </div>
      </>}
    </div>
  );
}
